import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBlogPosts, getTours } from '../services/api';
import { imgErrorFallback } from '../utils/imageFallback';
import { ArrowRight } from 'lucide-react';

/**
 * Renders related blog posts and related tours at the bottom of an article/tour page.
 * `excludeSlug` (blog or tour slug) is filtered out.
 * `matchCategory` (blog category name) is preferred for blog matching.
 * `matchKeyphrase` optionally boosts blog matches whose focusKeyphrase / title contains the value.
 */
const RelatedContent = ({ excludeSlug, matchCategory, matchKeyphrase, showTours = true, showBlogs = true, tourLimit = 3, blogLimit = 3, heading = 'İlgili İçerikler' }) => {
  const [blogs, setBlogs] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const promises = [];
      if (showBlogs) promises.push(getBlogPosts(50).catch(() => ({ data: [] })));
      else promises.push(Promise.resolve({ data: [] }));
      if (showTours) promises.push(getTours({ limit: 20 }).catch(() => ({ data: [] })));
      else promises.push(Promise.resolve({ data: [] }));

      const [bRes, tRes] = await Promise.all(promises);
      const bAll = (bRes.data || []).filter((p) => p.slug !== excludeSlug);

      // Score & sort blogs
      const key = (matchKeyphrase || '').toLowerCase();
      const scored = bAll.map((p) => {
        let score = 0;
        if (matchCategory && p.category === matchCategory) score += 5;
        const hay = `${p.title || ''} ${p.focusKeyphrase || ''} ${p.excerpt || ''}`.toLowerCase();
        if (key && hay.includes(key)) score += 3;
        return { p, score };
      });
      scored.sort((a, b) => b.score - a.score);
      setBlogs(scored.slice(0, blogLimit).map((s) => s.p));

      const tAll = (tRes.data || []).filter((t) => t.slug !== excludeSlug);
      setTours(tAll.slice(0, tourLimit));
      setLoading(false);
    };
    load();
  }, [excludeSlug, matchCategory, matchKeyphrase]);

  if (loading) return null;
  if (blogs.length === 0 && tours.length === 0) return null;

  return (
    <section className="mt-16 border-t border-gray-200 pt-12" data-testid="related-content">
      <h2 className="text-2xl md:text-3xl font-bold text-[#1B1B1B] mb-8" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        {heading}
      </h2>

      {blogs.length > 0 && (
        <>
          <h3 className="text-xs uppercase tracking-widest font-semibold text-[#3D4A26] mb-4">İlgili Yazılar</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {blogs.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`related-blog-${p.slug}`} className="group">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 mb-3">
                  <img src={p.image} alt={p.imageAlt || p.title} onError={imgErrorFallback} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                {p.category && <span className="text-[10px] uppercase tracking-widest font-semibold text-[#3D4A26] block mb-1">{p.category}</span>}
                <h4 className="font-bold text-[#1B1B1B] leading-snug text-base group-hover:text-[#3D4A26]">{p.title}</h4>
                {p.excerpt && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.excerpt}</p>}
              </Link>
            ))}
          </div>
        </>
      )}

      {tours.length > 0 && (
        <>
          <h3 className="text-xs uppercase tracking-widest font-semibold text-[#3D4A26] mb-4">İlgili Turlar</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tours.map((t) => (
              <Link key={t.id} to={`/tur/${t.slug}`} data-testid={`related-tour-${t.slug}`} className="group">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 mb-3">
                  <img src={t.heroImage} alt={t.heroImageAlt || t.title} onError={imgErrorFallback} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <h4 className="font-bold text-[#1B1B1B] leading-snug text-base group-hover:text-[#3D4A26]">{t.title}</h4>
                <p className="text-xs text-gray-500 mt-1">{t.nights} gece · {t.location}</p>
                <p className="text-sm font-bold text-[#1B1B1B] mt-2">
                  {t.currency}{new Intl.NumberFormat('tr-TR').format(t.salePrice)}
                  <span className="ml-1 text-xs font-normal text-gray-500">/ kişi</span>
                </p>
                <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#3D4A26]">Detay <ArrowRight size={11} /></span>
              </Link>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default RelatedContent;
